import React from "react";
import LegalPageShell, { Section } from "./LegalPageShell";

export default function PrivacyPolicy() {
  return (
    <LegalPageShell eyebrow="Legal" title="Privacy Policy" updated="February 2026">
      <p>
        NewDayStays Hospitality (&quot;we&quot;, &quot;us&quot;) respects your privacy. This policy explains what
        information we collect, how we use it, and the choices you have. It applies to newdaystays.in and any
        bookings made with us.
      </p>

      <Section title="1. Information we collect">
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Account information:</strong> name, email, phone number, password (stored as an irreversible bcrypt hash).</li>
          <li><strong>Booking information:</strong> check-in/out dates, guest count, property selected, special requests, ID details collected at check-in.</li>
          <li><strong>Payment information:</strong> handled entirely by <strong>Razorpay</strong>. We do not see, store, or process your card, UPI, or bank details on our servers.</li>
          <li><strong>Usage data:</strong> browser type, IP address, pages viewed, and referral source, collected via server logs and PostHog product analytics.</li>
        </ul>
      </Section>

      <Section title="2. How we use your information">
        <ul className="list-disc pl-6 space-y-2">
          <li>To process and manage your booking</li>
          <li>To send you booking confirmations, receipts, pre-arrival info, and post-stay follow-ups</li>
          <li>To respond to enquiries you send us via the contact form, email, or WhatsApp</li>
          <li>To comply with Indian hospitality law, including foreigner registration (Form C) where applicable</li>
          <li>To improve our website, offerings, and guest experience through anonymised analytics</li>
        </ul>
      </Section>

      <Section title="3. Sharing your information">
        <p>We share personal information only with:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Razorpay</strong> — to process payments (governed by Razorpay's own privacy policy)</li>
          <li><strong>Property hosts / caretakers</strong> — the minimum details required to receive you at check-in (name, arrival time, phone)</li>
          <li><strong>Government authorities</strong> — when legally required (e.g., FRRO Form C for foreign guests)</li>
        </ul>
        <p>
          We <strong>never sell</strong> your personal data to advertisers or third-party marketers.
        </p>
      </Section>

      <Section title="4. Data retention">
        <p>
          Booking records are retained for <strong>7 years</strong> to comply with Indian tax and hospitality
          record-keeping laws. Account data is retained as long as your account is active. You may request
          deletion at any time (see section 6).
        </p>
      </Section>

      <Section title="5. Cookies & analytics">
        <p>
          We use essential cookies to keep you signed in and remember your preferences. We use PostHog to
          understand how visitors use our website; this data is aggregated and anonymised. You can disable
          cookies in your browser, though parts of the site may not function correctly.
        </p>
      </Section>

      <Section title="6. Your rights">
        <p>You have the right to:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Access the personal data we hold about you</li>
          <li>Request correction of inaccurate data</li>
          <li>Request deletion of your account and associated data</li>
          <li>Withdraw consent for marketing communications at any time</li>
        </ul>
        <p>
          To exercise any of these rights, email us at{" "}
          <a href="mailto:newdaystay@gmail.com" className="underline">newdaystay@gmail.com</a>.
        </p>
      </Section>

      <Section title="7. Data security">
        <p>
          We follow industry-standard practices to protect your data: HTTPS everywhere, bcrypt-hashed passwords,
          JWT-based session tokens, secure MongoDB storage on managed infrastructure, and PCI-DSS-compliant
          payment processing via Razorpay. No system is 100% secure, but we take reasonable steps to protect you.
        </p>
      </Section>

      <Section title="8. Children">
        <p>
          Our website is not intended for children under 18. We do not knowingly collect personal information
          from minors. If you believe a child has provided us with data, please contact us and we will delete it.
        </p>
      </Section>

      <Section title="9. Grievance officer">
        <p>
          In accordance with the Information Technology Act, 2000 and rules made thereunder, the grievance
          officer for NewDayStays is:
        </p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Name: The Founder, NewDayStays Hospitality</li>
          <li>Email: newdaystay@gmail.com</li>
          <li>Phone / WhatsApp: +91 63607 34483</li>
          <li>Response time: within 30 days of a written complaint</li>
        </ul>
      </Section>

      <Section title="10. Changes to this policy">
        <p>
          We may update this policy occasionally. The updated version will be posted here with a new
          &quot;Last updated&quot; date.
        </p>
      </Section>
    </LegalPageShell>
  );
}
